import type { ActiveExecution, AgentSession } from "@/stores/companion-store";
import type { AgentCursor as AgentCursorData } from "@/stores/canvas-store";
import { useCanvasStore } from "@/stores/canvas-store";
import {
  cursorContentText,
  cursorToolCallText,
} from "./agent-cursor-display-status";
import { getThinkingPhrase } from "./cursor-thinking-text";
import { isVisibleAgentCursorWork } from "./agent-work-visibility";

const MAX_DOCK_AVATAR_OVERLAP = 14;

export function dockAvatarOverlap(count: number, compact: boolean) {
  if (count <= 1) return 0;
  const base = compact ? 12 : 12;
  return Math.min(MAX_DOCK_AVATAR_OVERLAP, base + (count - 2));
}

export function dockAvatarScale(count: number) {
  if (count >= 7) return 0.88;
  if (count >= 5) return 0.92;
  if (count >= 3) return 0.96;
  return 1;
}

export function cursorStatusText(cursor: AgentCursorData): string {
  if (cursor.error) return cursor.error;
  if (cursor.contentFull) return cursorContentText(cursor.contentFull);
  if (cursor.content) return cursorContentText(cursor.content);
  const toolCall = cursorToolCallText(cursor);
  if (toolCall) return toolCall;
  return getThinkingPhrase(`${cursor.agentId}:${cursor.name}`);
}

export function getDockCursors(cursors: Map<string, AgentCursorData>) {
  return [...cursors.values()]
    .filter(
      (cursor) => !cursor.pointTarget && isVisibleAgentCursorWork(cursor),
    )
    .sort((a, b) => {
      const frozenDelta = Number(a.frozen) - Number(b.frozen);
      if (frozenDelta !== 0) return frozenDelta;
      return new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime();
    });
}

export function isStoppableExecution(execution?: ActiveExecution): boolean {
  return (
    execution?.status === "running" || execution?.status === "waiting_approval"
  );
}

export function isReadyExecution(execution?: ActiveExecution): boolean {
  return (
    execution?.status === "paused" ||
    execution?.status === "completed" ||
    execution?.status === "cancelled"
  );
}

export function isFailedExecution(execution?: ActiveExecution): boolean {
  return execution?.status === "failed";
}

export function agentNameKey(name: string | undefined): string {
  return name?.split(" · ")[0]?.trim().toLowerCase() ?? "";
}

export function resolveCursorExecution(
  cursor: AgentCursorData | undefined,
  executions: Map<string, ActiveExecution>,
  agentSessions: Record<string, AgentSession>,
): ActiveExecution | undefined {
  if (!cursor) return undefined;
  const directExecution = executions.get(cursor.executionId ?? cursor.agentId);
  const runtimeSessionId =
    cursor.sessionId ?? directExecution?.runtimeSessionId;
  if (runtimeSessionId) {
    const sessionExecutions = [...executions.values()].filter(
      (execution) =>
        execution.runtimeSessionId === runtimeSessionId &&
        (!execution.spaceId || execution.spaceId === cursor.spaceId),
    );
    if (directExecution && isStoppableExecution(directExecution))
      return directExecution;
    return (
      sessionExecutions.find(isStoppableExecution) ??
      directExecution ??
      sessionExecutions[0]
    );
  }
  if (directExecution) return directExecution;

  const cursorKey = agentNameKey(cursor.name);
  const linkedSession = cursorKey
    ? agentSessions[`${cursorKey}:${cursor.spaceId}`]
    : undefined;
  const linkedExecution = executions.get(linkedSession?.executionId ?? "");
  if (linkedExecution) return linkedExecution;

  for (const execution of executions.values()) {
    if (!isStoppableExecution(execution)) continue;
    if (execution.spaceId && execution.spaceId !== cursor.spaceId) continue;
    const executionKey = agentNameKey(
      execution.agentName ?? execution.agentKey,
    );
    if (cursorKey && executionKey === cursorKey) return execution;
  }

  if (cursor.frozen) {
    for (const execution of executions.values()) {
      if (execution.spaceId && execution.spaceId !== cursor.spaceId) continue;
      const executionKey = agentNameKey(
        execution.agentName ?? execution.agentKey,
      );
      if (cursorKey && executionKey === cursorKey) return execution;
    }
  }

  return undefined;
}

export function resolveCursorExecutionId(
  cursor: AgentCursorData | undefined,
  executions: Map<string, ActiveExecution>,
  agentSessions: Record<string, AgentSession>,
): string | null {
  const execution = resolveCursorExecution(cursor, executions, agentSessions);
  return execution && isStoppableExecution(execution) ? execution.id : null;
}

export function defaultNavigateToCursorFrame(cursor: AgentCursorData) {
  if (!cursor.frozen || !cursor.targetFrameId) return;
  if (!useCanvasStore.getState().navigateToFrame(cursor.targetFrameId)) {
    window.setTimeout(() => {
      useCanvasStore.getState().navigateToFrame(cursor.targetFrameId!);
    }, 200);
  }
}
